// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Dialog component for adjusting inventory quantities.
 * Handles both increasing and decreasing item quantities at specific locations.
 */
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { useInventoryCheck } from '@/inventory/hooks';
import { invalidateInventory } from '@/inventory/utilities/invalidate-inventory';
import { updateInventory } from '@/items/api/update-inventory';
import { FormBulkItemSearch } from '@/items/components/form-bulk-item-search';
import { ADJUSTMENT_REASONS } from '@/items/constants/inventory-reasons';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  styled,
  toggleButtonClasses,
  toggleButtonGroupClasses,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonClasses.root}.${toggleButtonGroupClasses.grouped}`]: {
    border: 0,
    margin: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
  },
}));

const adjustInventorySchema = z.object({
  location: z.object(
    {
      id: z.string(),
      key: z.string(),
      name: z.string(),
    },
    { required_error: 'Location is required' }
  ),
  item: z.object(
    {
      id: z.string(),
      key: z.string(),
      name: z.string(),
    },
    { required_error: 'Item is required' }
  ),
  action: z.enum(['increase', 'decrease'], {
    required_error: 'Action is required',
  }),
  quantity: z
    .string({ required_error: 'Quantity is required' })
    .min(1, 'Quantity must be greater than 0')
    .refine((value) => Number(value) >= 1, 'Quantity must be greater than 0'),
  reason: z
    .string({ required_error: 'Reason is required' })
    .min(1, 'Reason is required'),
  note: z.string().optional(),
});

type AdjustInventoryFormData = z.infer<typeof adjustInventorySchema>;

interface Props {
  isOpen: boolean;
  item?: SparseItem;
  location?: SparseLocation;
  onClose: () => void;
}

export function AdjustInventoryDialog(props: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const { control, handleSubmit, reset, setError } =
    useForm<AdjustInventoryFormData>({
      resolver: zodResolver(adjustInventorySchema),
      defaultValues: {
        location: props.location,
        item: props.item,
        action: 'increase',
        quantity: '',
        reason: '',
      },
    });

  const watchedValues = useWatch({
    control,
    name: ['location', 'item'],
  });

  const selectedLocation = watchedValues[0] as SparseLocation | undefined;
  const selectedItem = watchedValues[1] as SparseItem | undefined;

  const { inventoryMap, isLoading, isFetching } = useInventoryCheck(
    selectedItem?.key || '',
    { includeNonInventory: true }
  );

  const availableQuantity =
    selectedLocation?.id && selectedItem?.id
      ? (inventoryMap.get(selectedLocation.id)?.get(selectedItem.id) ?? 0)
      : 0;

  useEffect(() => {
    if (props.isOpen) {
      reset({
        item: props.item,
        location: props.location,
        action: 'increase',
        quantity: '',
        reason: '',
      });
    }
  }, [props.isOpen, props.location, props.item, reset]);

  const adjustInventoryMutation = useMutation({
    mutationFn: updateInventory,
    onSuccess: () => {
      toast.showToast({
        element: <Typography>Inventory adjusted successfully</Typography>,
        severity: 'success',
        duration: 3000,
      });
      invalidateInventory(queryClient);
      handleClose();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);
      if (
        apiError.apiStatusCode === ERROR_CODE_MAPPING.part_invalid_quantity ||
        apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.inventory_removal_exceeds_stock
      ) {
        setErrorMessage('There is insufficient quantity at this location');
      } else {
        setErrorMessage(
          'An error occurred attempting to adjust your inventory'
        );
      }
    },
  });

  const handleClose = () => {
    setErrorMessage(undefined);
    props.onClose();
  };

  const handleCreateLocation = (name: string) => {
    const url = getNewLocationURL(name);
    window.open(url, '_blank');
  };

  const onSubmit = (data: AdjustInventoryFormData) => {
    const adjustedQuantity =
      data.action === 'decrease'
        ? -Number(data.quantity)
        : Number(data.quantity);

    if (adjustedQuantity == 0) {
      setError('quantity', {
        type: 'validate',
        message: `Quantity must be greater than 0`,
      });
      return;
    }

    if (data.action === 'decrease') {
      const locationInventory =
        inventoryMap.get(data.location.id)?.get(data.item.id) ?? 0;
      if (-adjustedQuantity > locationInventory) {
        setError('quantity', {
          type: 'validate',
          message: `You've exceeded the maximum amount by ${((locationInventory + adjustedQuantity) * -1).toLocaleString()} units`,
        });
        return;
      }
    }

    adjustInventoryMutation.mutate({
      locationKey: data.location.key,
      itemId: data.item.id,
      direction: data.action === 'decrease' ? 'DECREASE' : 'INCREASE',
      quantity: Number(data.quantity),
      reason: data.reason,
      note: data.note,
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={handleClose}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>Adjust inventory</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {errorMessage && (
            <Box sx={{ my: 2 }}>
              <Alert severity="error">{errorMessage}</Alert>
            </Box>
          )}
          <FormControl>
            <FormLocationSearch
              name="location"
              label="Location"
              control={control}
              onCreate={handleCreateLocation}
            />
          </FormControl>
          <FormControl>
            <FormBulkItemSearch name="item" label="Item" control={control} />
            {selectedLocation?.id && selectedItem?.key && (
              <FormHelperText>
                {isLoading || isFetching
                  ? 'Getting inventory at location...'
                  : `${availableQuantity} available at location`}
              </FormHelperText>
            )}
          </FormControl>

          <Controller
            name="action"
            control={control}
            defaultValue="increase"
            render={({ field, fieldState }) => {
              return (
                <FormControl
                  error={!!fieldState.error}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ width: '100%' }}
                  >
                    <Paper variant="outlined" sx={{ width: '100%' }}>
                      <StyledToggleButtonGroup
                        exclusive
                        fullWidth
                        size={'small'}
                        value={field.value}
                        onChange={(_, newValue) => {
                          if (newValue !== null) {
                            field.onChange(newValue);
                          }
                        }}
                      >
                        <ToggleButton value="increase">Increase</ToggleButton>
                        <ToggleButton value="decrease">Decrease</ToggleButton>
                      </StyledToggleButtonGroup>
                    </Paper>
                    <Tooltip
                      arrow
                      placement="right"
                      title={
                        <Stack>Increase or decrease inventory count</Stack>
                      }
                    >
                      <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                  {fieldState.error && (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />

          <Controller
            name="quantity"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={!!fieldState.error}>
                <TextField
                  {...field}
                  label="Quantity"
                  type="number"
                  size={'small'}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!fieldState.error}
                />
                {fieldState.error && (
                  <FormHelperText error>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            name="reason"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl error={!!fieldState.error}>
                <InputLabel id={'reason'}>Reason</InputLabel>
                <Select
                  size={'small'}
                  labelId={'reason'}
                  defaultValue={''}
                  label={'Reason'}
                  {...field}
                >
                  {ADJUSTMENT_REASONS.map((reason) => (
                    <MenuItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Notes"
                multiline
                rows={3}
                placeholder="Optional note about this adjustment"
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          variant="contained"
          color="primary"
          disabled={adjustInventoryMutation.isPending}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
